import { useEffect } from "react";
import type { NavigationContainerRef } from "@react-navigation/native";
import { useAppSelector } from "../redux/store";
import type RootStackParamList from "../navigation/RootStackParamList";
import { takePendingRoom } from "../navigation/pendingRoom";
import { getRoomById } from "../services/roomsService";

// Board #105: once the user is authenticated and the navigator is ready, consume
// a room id captured from the "View your conversation" email link
// (https://knexoapp.com/room/:roomId) and open that conversation.
//
// This is deliberately NOT React Navigation's `linking` config: auth resolves
// asynchronously and the app swaps between the Auth navigator and the main
// navigator on auth state, so a one-shot initial-URL parse would fire before
// `Call` exists (or before sign-in) and be lost. Holding the id in a module and
// consuming it the moment `name` becomes non-empty handles both the already
// signed-in and the sign-in-after-clicking cases.
export default function DeepLinkNavigator({
  navigationRef,
}: {
  navigationRef: NavigationContainerRef<RootStackParamList>;
}) {
  const isAuthed = useAppSelector((state) => !!state.user.name);

  useEffect(() => {
    if (!isAuthed) return;
    const roomId = takePendingRoom();
    if (!roomId) return;
    let alive = true;
    getRoomById(roomId)
      .then((room) => {
        if (alive && room && navigationRef.isReady()) {
          navigationRef.navigate("Call", { room });
        }
      })
      .catch(() => {
        // A missing/unreadable room just leaves the user where they are rather
        // than crashing — the email link should never hard-fail the app.
      });
    return () => {
      alive = false;
    };
  }, [isAuthed, navigationRef]);

  return null;
}
